import { ApolloLink, Observable } from '@apollo/client'
import { noop } from 'lodash-es'
import type { GqlOperationName } from 'gql/names.types'

export interface TrackOperationStatusCallbacks {
  onStartOperation: (operationName: GqlOperationName) => void
  onEndOperation: (operationName: GqlOperationName) => void
  onOperationError: (operationName: GqlOperationName, error: string | null) => void
}

interface Unsubscribable {
  unsubscribe: () => void
}

export const createTrackOperationStatusLink = (
  { onStartOperation, onEndOperation, onOperationError }: TrackOperationStatusCallbacks,
  predicate?: (operation: ApolloLink.Operation) => boolean,
) => {
  return new ApolloLink((operation, forward) => {
    const operationName = operation.operationName as GqlOperationName

    const observable = forward(operation)

    if (predicate && !predicate(operation)) {
      return observable
    }

    onStartOperation(operationName)

    return new Observable<ApolloLink.Result>((observer) => {
      // Stands in until `subscribe` returns: a link below that completes synchronously runs this
      // teardown before the assignment lands, and reading the real subscription there would throw.
      let subscription: Unsubscribable = { unsubscribe: noop }

      subscription = observable.subscribe({
        next: (result) => {
          const error = result.errors?.[0] ?? null

          if (error) {
            onOperationError(operationName, error.message)
          }
          observer.next(result)
        },
        error: (error: Error) => {
          onOperationError(operationName, error.message)
          observer.error(error)
        },
        complete: () => {
          observer.complete()
        },
      })

      return () => {
        onEndOperation(operationName)
        subscription.unsubscribe()
      }
    })
  })
}
